import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalHeader } from 'client/js/util/modal';
import { useQueryClient } from '@tanstack/react-query';
import { fetchJSON } from 'client/js/util/rest_utils';

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const DiskUsageItem = ({ item, totalSize, onNavigate, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const percentage = totalSize > 0 ? (item.size / totalSize * 100) : 0;

  const hasChildren = item.children && item.children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleNavigate = (e) => {
    e.stopPropagation();
    if (item.type === 'directory' && onNavigate) {
      onNavigate(item.path);
    }
  };

  return (
    <div className="disk-usage-item">
      <div
        className={`disk-usage-item-header ${hasChildren ? 'has-children' : ''} ${item.type === 'directory' ? 'directory' : 'file'}`}
        onClick={handleToggle}
        style={{ paddingLeft: `${level * 20 + 10}px` }}
      >
        <div className="disk-usage-item-info">
          {hasChildren && (
            <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} mr-2`} style={{ width: '12px' }} />
          )}
          {!hasChildren && <span style={{ width: '28px', display: 'inline-block' }} />}
          <i className={`fa ${item.type === 'directory' ? 'fa-folder' : 'fa-file-o'} mr-2`} />
          <span className="disk-usage-item-name">{item.name}</span>
          {item.type === 'directory' && onNavigate && (
            <button
              className="btn btn-sm btn-link ml-2"
              onClick={handleNavigate}
              title="Zu diesem Verzeichnis wechseln"
            >
              <i className="fa fa-arrow-right" />
            </button>
          )}
        </div>

        <div className="disk-usage-item-stats">
          <span className="disk-usage-size">{formatBytes(item.size)}</span>
          <span className="disk-usage-percentage ml-2">({percentage.toFixed(1)}%)</span>
        </div>
      </div>

      <div className="disk-usage-bar" style={{ marginLeft: `${level * 20 + 10}px` }}>
        <div
          className={`disk-usage-bar-fill ${percentage > 10 ? 'visible' : ''}`}
          style={{ width: `${Math.max(percentage, 0.5)}%` }}
        />
      </div>

      {isExpanded && hasChildren && (
        <div className="disk-usage-children">
          {item.children.map((child, index) => (
            <DiskUsageItem
              key={index}
              item={child}
              totalSize={totalSize}
              onNavigate={onNavigate}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

DiskUsageItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    children: PropTypes.array,
  }).isRequired,
  totalSize: PropTypes.number.isRequired,
  onNavigate: PropTypes.func,
  level: PropTypes.number,
};

const DiskUsageModal = ({ paths, currentPath, onClose, baseApiUrl, onNavigate }) => {
  const [sortBy, setSortBy] = useState('size'); // 'size' or 'name'
  const [sortDirection, setSortDirection] = useState('desc');
  const [isScanning, setIsScanning] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [currentScanPath, setCurrentScanPath] = useState('');
  const [scannedCount, setScannedCount] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const abortControllerRef = useRef(null);
  const queryClient = useQueryClient();

  const targetPath = paths && paths.length > 0 ? paths[0] : currentPath;
  const apiUrl = baseApiUrl || '/usercp/filemanager/api';

  // Recursive directory scanner
  const scanDirectory = useCallback(async (path, signal, parentUpdateCallback = null) => {
    setCurrentScanPath(path);

    try {
      // Try to use cached data first
      const cachedData = queryClient.getQueryData(['filemanager', 'list', path, apiUrl]);

      let files;
      if (cachedData?.files) {
        files = cachedData.files;
      } else {
        const response = await fetchJSON(`${apiUrl}/list?path=${encodeURIComponent(path)}`, {
          method: 'GET',
          signal
        });
        files = response.files || [];
      }

      const node = {
        path: path,
        name: path.split('/').pop() || path,
        type: 'directory',
        size: 0,
        children: []
      };

      // Process all items in this directory
      for (const file of files) {
        setScannedCount(prev => prev + 1);

        if (signal.aborted) {
          throw new Error('Scan aborted');
        }

        if (file.type === 'directory') {
          // Recursively scan subdirectory with callback to update parent
          const updateChild = (childNode) => {
            const existingIndex = node.children.findIndex(c => c.path === childNode.path);
            if (existingIndex >= 0) {
              node.children[existingIndex] = childNode;
            } else {
              node.children.push(childNode);
            }
            node.children.sort((a, b) => b.size - a.size);

            // Recalculate parent size
            node.size = node.children.reduce((sum, child) => sum + child.size, 0);

            // Notify parent to update
            if (parentUpdateCallback) {
              parentUpdateCallback({...node});
            }
          };

          const childNode = await scanDirectory(file.path, signal, updateChild);
          updateChild(childNode);
        } else {
          // Add file
          const fileNode = {
            path: file.path,
            name: file.name,
            type: 'file',
            size: file.size || 0
          };
          node.children.push(fileNode);
          node.size += fileNode.size;
          setTotalSize(prev => prev + fileNode.size);

          // Notify parent about update every few files for live updates
          if (parentUpdateCallback && node.children.length % 5 === 0) {
            node.children.sort((a, b) => b.size - a.size);
            parentUpdateCallback({...node});
          }
        }
      }

      // Sort children by size (descending) by default
      node.children.sort((a, b) => b.size - a.size);

      return node;
    } catch (err) {
      if (err.name === 'AbortError' || err.message === 'Scan aborted') {
        throw err;
      }
      console.error(`Error scanning ${path}:`, err);
      // Return partial node on error
      return {
        path: path,
        name: path.split('/').pop() || path,
        type: 'directory',
        size: 0,
        children: [],
        error: err.message
      };
    }
  }, [apiUrl, queryClient]);

  // Start scanning on mount
  useEffect(() => {
    abortControllerRef.current = new AbortController();

    const startScan = async () => {
      setIsScanning(true);
      setError(null);
      setScannedCount(0);
      setTotalSize(0);

      try {
        // Root level update callback for live updates
        const rootUpdateCallback = (updatedNode) => {
          setData({...updatedNode});
        };

        const result = await scanDirectory(targetPath, abortControllerRef.current.signal, rootUpdateCallback);
        setData(result);
        setIsScanning(false);
      } catch (err) {
        if (err.name !== 'AbortError' && err.message !== 'Scan aborted') {
          setError(err);
          setIsScanning(false);
        }
      }
    };

    startScan();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [targetPath, scanDirectory]);

  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsScanning(false);
    }
  };

  const sortedData = useMemo(() => {
    if (!data || !data.children) return null;

    const sorted = [...data.children].sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'size') {
        comparison = b.size - a.size;
      } else {
        comparison = a.name.localeCompare(b.name);
      }

      return sortDirection === 'asc' ? -comparison : comparison;
    });

    return { ...data, children: sorted };
  }, [data, sortBy, sortDirection]);

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection(newSortBy === 'size' ? 'desc' : 'asc');
    }
  };

  const handleNavigate = (path) => {
    if (onNavigate) {
      onNavigate(path);
      onClose();
    }
  };

  return (
    <Modal isOpen large onRequestClose={onClose}>
      <ModalHeader title="Speicherplatzbelegung" onRequestClose={onClose} />

      <div className="modal-body">
        {isScanning && (
          <div className="disk-usage-scanning">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm text-primary mr-3" role="status">
                  <span className="sr-only">Scanne...</span>
                </div>
                <div>
                  <div className="font-weight-bold">Analysiere Verzeichnisstruktur...</div>
                  <div className="small text-muted text-truncate" style={{ maxWidth: '400px' }}>
                    {currentScanPath || targetPath}
                  </div>
                </div>
              </div>
              <button className="btn btn-sm btn-outline-danger" onClick={handleAbort}>
                <i className="fa fa-stop mr-1" />
                Abbrechen
              </button>
            </div>

            <div className="disk-usage-scan-stats">
              <div className="row text-center">
                <div className="col-6">
                  <div className="h4 mb-1">{scannedCount.toLocaleString()}</div>
                  <div className="small text-muted">Elemente gescannt</div>
                </div>
                <div className="col-6">
                  <div className="h4 mb-1">{formatBytes(totalSize)}</div>
                  <div className="small text-muted">Gesamtgröße (bisher)</div>
                </div>
              </div>
            </div>

            {data && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="small text-muted">
                    Zwischenergebnisse (Scan läuft noch...)
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      className={`btn btn-outline-secondary ${sortBy === 'size' ? 'active' : ''}`}
                      onClick={() => handleSort('size')}
                    >
                      <i className="fa fa-sort-amount-desc mr-1" />
                      Größe
                      {sortBy === 'size' && (
                        <i className={`fa fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-1`} />
                      )}
                    </button>
                    <button
                      className={`btn btn-outline-secondary ${sortBy === 'name' ? 'active' : ''}`}
                      onClick={() => handleSort('name')}
                    >
                      <i className="fa fa-sort-alpha-asc mr-1" />
                      Name
                      {sortBy === 'name' && (
                        <i className={`fa fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-1`} />
                      )}
                    </button>
                  </div>
                </div>
                <div className="disk-usage-list" style={{ maxHeight: '300px' }}>
                  {sortedData?.children && sortedData.children.length > 0 && (
                    sortedData.children.map((item, index) => (
                      <DiskUsageItem
                        key={index}
                        item={item}
                        totalSize={sortedData.size}
                        onNavigate={handleNavigate}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-triangle mr-2" />
            {error.message || 'Fehler beim Laden der Speicherplatzbelegung'}
          </div>
        )}

        {!isScanning && sortedData && (
          <>
            <div className="disk-usage-header mb-3">
              <h6 className="mb-2">
                <i className="fa fa-folder-open mr-2" />
                {sortedData.path}
              </h6>
              <div className="d-flex justify-content-between align-items-center">
                <div className="disk-usage-summary">
                  <strong>Gesamtgröße:</strong> {formatBytes(sortedData.size)}
                  {sortedData.children && sortedData.children.length > 0 && (
                    <span className="ml-3 text-muted">
                      ({sortedData.children.length} {sortedData.children.length === 1 ? 'Element' : 'Elemente'})
                    </span>
                  )}
                </div>

                <div className="btn-group btn-group-sm">
                  <button
                    className={`btn btn-outline-secondary ${sortBy === 'size' ? 'active' : ''}`}
                    onClick={() => handleSort('size')}
                  >
                    <i className="fa fa-sort-amount-desc mr-1" />
                    Größe
                    {sortBy === 'size' && (
                      <i className={`fa fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-1`} />
                    )}
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${sortBy === 'name' ? 'active' : ''}`}
                    onClick={() => handleSort('name')}
                  >
                    <i className="fa fa-sort-alpha-asc mr-1" />
                    Name
                    {sortBy === 'name' && (
                      <i className={`fa fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-1`} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="disk-usage-list">
              {sortedData.children && sortedData.children.length > 0 ? (
                sortedData.children.map((item, index) => (
                  <DiskUsageItem
                    key={index}
                    item={item}
                    totalSize={sortedData.size}
                    onNavigate={handleNavigate}
                  />
                ))
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fa fa-folder-open-o fa-3x mb-3" />
                  <p>Dieses Verzeichnis ist leer</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="modal-footer">
        {isScanning && (
          <div className="mr-auto text-muted small">
            <i className="fa fa-info-circle mr-1" />
            Scan läuft im Hintergrund. Sie können das Fenster schließen.
          </div>
        )}
        <button className="btn btn-secondary" onClick={onClose}>
          {isScanning ? 'Minimieren' : 'Schließen'}
        </button>
      </div>
    </Modal>
  );
};

DiskUsageModal.propTypes = {
  paths: PropTypes.array,
  currentPath: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
  onNavigate: PropTypes.func,
};

export default DiskUsageModal;

